import React, { useEffect, useRef, useState } from "react";
import { api, streamChat } from "../lib/api";
import { toast } from "sonner";
import { KurtMark } from "../components/KurtMark";
import { Send, Sparkles, SlidersHorizontal, Plus, Trash2 } from "lucide-react";

const SESSION_KEY = "kurt_session_id";
function getSession() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) { s = "sess-" + Math.random().toString(36).slice(2); localStorage.setItem(SESSION_KEY, s); }
  return s;
}

const PILLS = [
  "Find UK cafés that just expanded and start outreach",
  "Draft a follow-up for leads who went quiet",
  "Invent a guerrilla campaign nobody in our category is doing",
  "Grow our readership toward the target",
];

export const TalkToKurt = ({ settings, goOpportunities }) => {
  const [session] = useState(getSession);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.getChatHistory(session).then((h) => {
      setMessages(h.map((m) => ({ role: m.role, content: m.content })));
    });
  }, [session]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }]);

    // First: does KURT need to actually DO something (hunt prospects)?
    try {
      const act = await api.act(session, msg);
      if (act.intent === "source_prospects") {
        setMessages((m) => [...m, { role: "assistant", content: act.reply }]);
        toast.success(`KURT found ${act.count} prospects — opening Opportunities.`);
        setBusy(false);
        setTimeout(() => goOpportunities && goOpportunities(), 900);
        return;
      }
    } catch (e) { /* fall through to normal chat */ }

    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    try {
      await streamChat(session, msg, (delta) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + delta };
          return copy;
        });
      });
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "I hit a snag reaching my brain. Try again in a moment." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const empty = messages.length === 0;

  const newChat = () => {
    const s = "sess-" + Math.random().toString(36).slice(2);
    localStorage.setItem(SESSION_KEY, s);
    window.location.reload();
  };
  const clearChat = async () => {
    if (!window.confirm("Clear this conversation? This can't be undone.")) return;
    await api.clearChat(session);
    setMessages([]);
    toast.success("Conversation cleared.");
  };

  return (
    <div data-testid="screen-talk" className="max-w-3xl mx-auto flex flex-col" style={{ height: "calc(100vh - 12rem)" }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-emerald-400 font-semibold tracking-wide">TALK TO KURT</p>
          <h1 className="kz-head text-2xl font-bold mt-1">Tell me what you want done.</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="new-chat-btn" onClick={newChat} className="flex items-center gap-1.5 text-xs font-semibold text-slate-300 px-3 py-1.5 rounded-full border border-white/10 bg-[#121824] hover:bg-white/[0.06] transition-all"><Plus size={13} className="text-emerald-400" /> New chat</button>
          {!empty && <button data-testid="clear-chat-btn" onClick={clearChat} className="flex items-center gap-1.5 text-xs font-semibold text-red-400/80 px-3 py-1.5 rounded-full border border-red-500/20 hover:bg-red-500/10 transition-all"><Trash2 size={13} /> Clear</button>}
          <div className="hidden sm:flex items-center gap-2 text-xs text-slate-400 px-3 py-1.5 rounded-full border border-white/10 bg-[#121824]">
            <SlidersHorizontal size={13} className="text-emerald-400" />
            <span>{settings?.tone} · discount cap {settings?.max_discount}%</span>
          </div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto pr-1 space-y-5">
        {empty && (
          <div className="kz-fade-up flex flex-col items-center text-center pt-8">
            <KurtMark size={56} />
            <h2 className="kz-head text-xl font-bold mt-5">I don't just advise — I do the work.</h2>
            <p className="text-slate-400 text-sm mt-2 max-w-md leading-relaxed">
              Ask me to find buyers, launch a campaign, or chase quiet leads. I'll execute what I can, queue what needs your nod, and hand you the rest ready to go.
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <MessageBubble key={i} role={m.role} content={m.content} streaming={busy && i === messages.length - 1 && m.role === "assistant"} />
        ))}
      </div>

      {empty && (
        <div className="flex flex-wrap gap-2 mt-4">
          {PILLS.map((p) => (
            <button
              key={p}
              data-testid={`pill-${p.slice(0, 10)}`}
              onClick={() => send(p)}
              className="flex items-center gap-1.5 text-xs text-slate-300 px-3 py-2 rounded-full border border-white/10 bg-[#121824] hover:border-emerald-500/40 hover:text-slate-50 transition-all hover:scale-[1.02]"
            >
              <Sparkles size={12} className="text-emerald-400" /> {p}
            </button>
          ))}
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="mt-4 flex items-end gap-2 rounded-2xl border border-white/12 bg-[#121824] p-2 focus-within:border-emerald-500/40 transition-all"
      >
        <textarea
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={1}
          placeholder="Tell KURT to do something…"
          className="flex-1 bg-transparent resize-none outline-none text-sm text-slate-100 placeholder:text-slate-500 px-3 py-2 max-h-32"
        />
        <button
          data-testid="chat-send-btn"
          type="submit"
          disabled={busy || !input.trim()}
          className="w-10 h-10 rounded-xl bg-emerald-500 text-[#04140d] flex items-center justify-center hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:scale-105 active:scale-95"
        >
          <Send size={17} />
        </button>
      </form>
    </div>
  );
};

const MessageBubble = ({ role, content, streaming }) => {
  const isUser = role === "user";
  return (
    <div className={`kz-fade-up flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
      {!isUser && <KurtMark size={32} className="mt-0.5" />}
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
        isUser ? "bg-emerald-500 text-[#04140d] font-medium rounded-tr-sm" : "bg-[#1A2234] text-slate-200 border border-white/[0.06] rounded-tl-sm"
      }`}>
        {content}
        {streaming && !content && <span className="inline-flex gap-0.5"><Dot /><Dot d={0.2} /><Dot d={0.4} /></span>}
        {streaming && content && <span className="inline-block w-1.5 h-4 bg-emerald-400 ml-0.5 align-middle kz-blink" />}
      </div>
    </div>
  );
};

const Dot = ({ d = 0 }) => (
  <span className="w-1.5 h-1.5 rounded-full bg-slate-400" style={{ animation: "kz-dots 1.2s infinite", animationDelay: `${d}s` }} />
);
